import type { Permission } from '@bnn/shared';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import Layout from './components/Layout';
import { Spinner } from './components/ui';
import BarcodesPage from './pages/BarcodesPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ComingSoonPage from './pages/ComingSoonPage';
import CountIndexPage from './pages/CountIndexPage';
import CountPage from './pages/CountPage';
import HomePage from './pages/HomePage';
import ImportsPage from './pages/ImportsPage';
import ItemDetailPage from './pages/ItemDetailPage';
import ItemsPage from './pages/ItemsPage';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import SessionDetailPage from './pages/SessionDetailPage';
import SessionsPage from './pages/SessionsPage';
import SyncPage from './pages/SyncPage';
import UsersPage from './pages/UsersPage';
import WarehousesPage from './pages/WarehousesPage';

function RequireAuth({ children, perm }: { children: ReactNode; perm?: Permission }) {
  const { status, user, can } = useAuth();
  const loc = useLocation();
  if (status === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center text-muted">
        <Spinner className="size-8" />
      </div>
    );
  }
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (user?.must_change_password && loc.pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  if (perm && !can(perm)) return <NotFoundPage forbidden />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/change-password"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="items" element={<ItemsPage />} />
        <Route path="items/:code" element={<ItemDetailPage />} />
        <Route path="warehouses" element={<WarehousesPage />} />
        <Route path="imports" element={<RequireAuth perm="master.import"><ImportsPage /></RequireAuth>} />
        <Route path="users" element={<RequireAuth perm="users.manage"><UsersPage /></RequireAuth>} />
        <Route path="count" element={<RequireAuth perm="count.scan"><CountIndexPage /></RequireAuth>} />
        <Route path="count/:id" element={<RequireAuth perm="count.scan"><CountPage /></RequireAuth>} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="sessions/:id" element={<SessionDetailPage />} />
        <Route path="barcodes" element={<RequireAuth perm="barcodes.view"><BarcodesPage /></RequireAuth>} />
        <Route path="reports/*" element={<ComingSoonPage milestone="M4" title="รายงาน" />} />
        <Route path="sync" element={<RequireAuth perm="count.scan"><SyncPage /></RequireAuth>} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
